import { Modal } from '@components/modal';
import useHRForm from 'hooks/useHRForm';
import { type FC, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { manageTagSchema } from '../../form/ManageTagForm/schema';
import { ManageTagForm } from '../../form/ManageTagForm';
import {
  useCreateTag,
  useSetCustomProperty,
} from 'api/customProperty/customPropertyService';
import { t } from 'i18next';

interface AddTagModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleOpen: () => void;
}

const AddTagModal: FC<AddTagModalProps> = ({
  isOpen,
  handleClose,
  handleOpen,
}) => {
  const { methods } = useHRForm({
    schema: manageTagSchema,
  });

  const {
    mutateAsync: createTagMutate,
    data: tagData,
    isPending,
    isSuccess,
  } = useCreateTag();

  const { mutate: setCustomPropertyMutate } = useSetCustomProperty();

  const closeModal = (): void => {
    methods.reset({}, { keepValues: false });
    handleClose();
  };

  const switchUnderlineToWhitespace = (str: string): string => {
    return str.replace(/_/g, ' ');
  };

  const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleLinkTagToAsset = (): number[] => {
    const formData = methods.getValues();
    const assetParams = formData.buildings.map(
      (building: { name: string; value: string | number }) =>
        Number(building.value)
    );
    return assetParams;
  };

  const handleAddTag = async (): Promise<void> => {
    const formData = methods.getValues();
    const params = {
      name: formData.name,
      display_name: capitalizeFirstLetter(
        switchUnderlineToWhitespace(formData.name).toLowerCase()
      ),
      description: formData.description,
      data_type: 'STRING',
      is_required: false,
      is_searchable: true,
    };

    await createTagMutate(params);

    methods.clearErrors();
    methods.reset({}, { keepValues: false });
  };

  useEffect(() => {
    if (tagData) {
      const assetParams = handleLinkTagToAsset();
      // eslint-disable-next-line array-callback-return
      assetParams.map((assetId: number) => {
        const propertyDefinitionId = tagData?.id;
        const value = tagData?.data_type;

        if (propertyDefinitionId && value) {
          setCustomPropertyMutate({
            params: {
              property_definition_id: propertyDefinitionId,
              value,
            },
            assetId,
          });
        }
      });
    }
  }, [tagData]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      onOpen={handleOpen}
      actionBtns={{
        primary: {
          label: t('assetsPage.AddTagModal.title'),
          onClick: methods.handleSubmit(handleAddTag),

          isLoading: isPending,
        },
        secondary: { label: t('common.buttons.cancel') },
      }}
      title={t('assetsPage.AddTagModal.title')}
      minHeight={500}
    >
      <FormProvider {...methods}>
        <ManageTagForm methods={methods} />
      </FormProvider>
    </Modal>
  );
};
export default AddTagModal;
